import { useState } from 'react';
import { authAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

function RegisterPage({ onSwitchToLogin, onRegisterSuccess, onViewTerms, onViewPrivacy }) {
  const { login } = useAuth();
  const [form, setForm] = useState({
    username: '',
    password: '',
    passwordConfirm: '',
    name: '',
    email: '',
    phone: '',
    referralCode: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [usernameChecked, setUsernameChecked] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (name === 'username') {
      setUsernameChecked(false);
      setUsernameAvailable(false);
    }
  };

  const checkUsername = async () => {
    if (!form.username || form.username.length < 4) {
      setError('아이디는 4자 이상이어야 합니다.');
      return;
    }
    if (!/^[a-z0-9_]+$/.test(form.username)) {
      setError('아이디는 영문소문자, 숫자, 언더스코어만 사용 가능합니다.');
      return;
    }

    try {
      const result = await authAPI.checkUsername(form.username);
      setUsernameChecked(true);
      setUsernameAvailable(result.data.available);
      setError(result.data.available ? '' : '이미 사용 중인 아이디입니다.');
    } catch {
      setError('중복 확인 중 오류가 발생했습니다.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!usernameChecked || !usernameAvailable) {
      setError('아이디 중복 확인을 해주세요.');
      return;
    }
    if (form.password.length < 8) {
      setError('비밀번호는 8자 이상이어야 합니다.');
      return;
    }
    if (!/[a-z]/.test(form.password)) {
      setError('비밀번호에 영문소문자를 포함해주세요.');
      return;
    }
    if (!/[0-9]/.test(form.password)) {
      setError('비밀번호에 숫자를 포함해주세요.');
      return;
    }
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(form.password)) {
      setError('비밀번호에 특수문자를 포함해주세요.');
      return;
    }
    if (form.password !== form.passwordConfirm) {
      setError('비밀번호가 일치하지 않습니다.');
      return;
    }
    if (!form.name.trim()) {
      setError('이름을 입력해주세요.');
      return;
    }
    if (!agreeTerms || !agreePrivacy) {
      setError('이용약관과 개인정보 처리방침에 동의해주세요.');
      return;
    }

    setLoading(true);
    try {
      const result = await authAPI.register({
        username: form.username,
        password: form.password,
        name: form.name,
        email: form.email || undefined,
        phone: form.phone || undefined,
        referralCode: form.referralCode || undefined,
      });

      if (result.success) {
        // 자동 로그인 시도
        const loginResult = await login(form.username, form.password);
        if (loginResult.success && onRegisterSuccess) {
          onRegisterSuccess();
          return;
        }
        // 자동 로그인 실패 시 기존 성공 화면 표시
        setSuccess(true);
      } else {
        setError(result.message || '회원가입에 실패했습니다.');
      }
    } catch (err) {
      setError(err.message || '서버 연결에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary to-purple-600 px-4">
        <div className="w-full max-w-md">
          <div className="bg-white dark:bg-[#111118] rounded-2xl shadow-2xl p-8 border border-slate-200 dark:border-[#282839] text-center">
            <div className="inline-flex items-center justify-center size-16 rounded-full bg-green-100 dark:bg-green-900/30 mb-4">
              <span className="material-symbols-outlined text-green-600 dark:text-green-400 text-4xl">check_circle</span>
            </div>
            <h2 className="text-2xl font-bold mb-2 text-slate-900 dark:text-white">회원가입 완료</h2>
            <p className="text-slate-600 dark:text-slate-400 mb-6">로그인하여 서비스를 이용해주세요.</p>
            <button
              onClick={onSwitchToLogin}
              className="w-full py-4 bg-primary text-white rounded-xl font-bold text-base shadow-lg shadow-primary/40 hover:opacity-90 transition-all"
            >
              로그인하기
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary to-purple-600 px-4 py-8">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center size-14 rounded-full bg-white dark:bg-white shadow-lg mb-3">
            <span className="material-symbols-outlined text-primary text-3xl">auto_awesome</span>
          </div>
          <h1 className="text-3xl font-black text-white mb-1">InstaAuto</h1>
          <p className="text-white/80">회원가입</p>
        </div>

        <div className="bg-white dark:bg-[#111118] rounded-2xl shadow-2xl p-8 border border-slate-200 dark:border-[#282839]">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* 아이디 */}
            <div className="space-y-1.5">
              <label className="text-sm font-semibold px-1 text-slate-900 dark:text-white block">아이디</label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 material-symbols-outlined text-xl">person</span>
                  <input
                    type="text"
                    name="username"
                    value={form.username}
                    onChange={handleChange}
                    placeholder="영문소문자, 숫자, _ (4~20자)"
                    required
                    autoFocus
                    className="w-full pl-10 pr-3 h-12 bg-slate-50 dark:bg-[#1c1c27] border border-slate-200 dark:border-[#3b3b54] rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 text-sm"
                  />
                </div>
                <button
                  type="button"
                  onClick={checkUsername}
                  className={`px-4 h-12 rounded-xl font-semibold text-sm whitespace-nowrap transition-all ${
                    usernameChecked && usernameAvailable
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                      : 'bg-slate-100 text-slate-700 dark:bg-[#1c1c27] dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-[#282839]'
                  }`}
                >
                  {usernameChecked && usernameAvailable ? '확인됨' : '중복확인'}
                </button>
              </div>
            </div>

            {/* 비밀번호 */}
            <div className="space-y-1.5">
              <label className="text-sm font-semibold px-1 text-slate-900 dark:text-white block">비밀번호</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 material-symbols-outlined text-xl">lock</span>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  placeholder="8자 이상, 영문소문자+숫자+특수문자"
                  required
                  className="w-full pl-10 pr-10 h-12 bg-slate-50 dark:bg-[#1c1c27] border border-slate-200 dark:border-[#3b3b54] rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                >
                  <span className="material-symbols-outlined text-xl">
                    {showPassword ? 'visibility' : 'visibility_off'}
                  </span>
                </button>
              </div>
              {form.password && (
                <div className="flex flex-wrap gap-x-3 gap-y-1 px-1 pt-1">
                  {[
                    { ok: form.password.length >= 8, label: '8자 이상' },
                    { ok: /[a-z]/.test(form.password), label: '영문소문자' },
                    { ok: /[0-9]/.test(form.password), label: '숫자' },
                    { ok: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(form.password), label: '특수문자' },
                  ].map(rule => (
                    <span key={rule.label} className={`flex items-center gap-0.5 text-xs font-medium ${rule.ok ? 'text-green-600 dark:text-green-400' : 'text-slate-400 dark:text-slate-500'}`}>
                      <span className="material-symbols-outlined text-sm">{rule.ok ? 'check_circle' : 'radio_button_unchecked'}</span>
                      {rule.label}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* 비밀번호 확인 */}
            <div className="space-y-1.5">
              <label className="text-sm font-semibold px-1 text-slate-900 dark:text-white block">비밀번호 확인</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 material-symbols-outlined text-xl">lock</span>
                <input
                  type={showPasswordConfirm ? "text" : "password"}
                  name="passwordConfirm"
                  value={form.passwordConfirm}
                  onChange={handleChange}
                  placeholder="비밀번호를 다시 입력하세요"
                  required
                  className="w-full pl-10 pr-10 h-12 bg-slate-50 dark:bg-[#1c1c27] border border-slate-200 dark:border-[#3b3b54] rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                >
                  <span className="material-symbols-outlined text-xl">
                    {showPasswordConfirm ? 'visibility' : 'visibility_off'}
                  </span>
                </button>
              </div>
              {form.passwordConfirm && (
                <div className="px-1 pt-1">
                  <span className={`flex items-center gap-0.5 text-xs font-medium ${form.password === form.passwordConfirm ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
                    <span className="material-symbols-outlined text-sm">{form.password === form.passwordConfirm ? 'check_circle' : 'cancel'}</span>
                    {form.password === form.passwordConfirm ? '비밀번호 일치' : '비밀번호 불일치'}
                  </span>
                </div>
              )}
            </div>

            {/* 이름 */}
            <div className="space-y-1.5">
              <label className="text-sm font-semibold px-1 text-slate-900 dark:text-white block">이름 <span className="text-red-500">*</span></label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 material-symbols-outlined text-xl">badge</span>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="이름을 입력하세요"
                  required
                  className="w-full pl-10 pr-3 h-12 bg-slate-50 dark:bg-[#1c1c27] border border-slate-200 dark:border-[#3b3b54] rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 text-sm"
                />
              </div>
            </div>

            {/* 이메일 */}
            <div className="space-y-1.5">
              <label className="text-sm font-semibold px-1 text-slate-900 dark:text-white block">이메일 <span className="text-slate-400 text-xs">(선택)</span></label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 material-symbols-outlined text-xl">mail</span>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="example@email.com"
                  className="w-full pl-10 pr-3 h-12 bg-slate-50 dark:bg-[#1c1c27] border border-slate-200 dark:border-[#3b3b54] rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 text-sm"
                />
              </div>
            </div>

            {/* 전화번호 */}
            <div className="space-y-1.5">
              <label className="text-sm font-semibold px-1 text-slate-900 dark:text-white block">전화번호 <span className="text-slate-400 text-xs">(선택)</span></label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 material-symbols-outlined text-xl">phone</span>
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="010-1234-5678"
                  className="w-full pl-10 pr-3 h-12 bg-slate-50 dark:bg-[#1c1c27] border border-slate-200 dark:border-[#3b3b54] rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 text-sm"
                />
              </div>
            </div>

            {/* 추천인 코드 */}
            <div className="space-y-1.5">
              <label className="text-sm font-semibold px-1 text-slate-900 dark:text-white block">추천인 코드 <span className="text-slate-400 text-xs">(선택)</span></label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 material-symbols-outlined text-xl">loyalty</span>
                <input
                  type="text"
                  name="referralCode"
                  value={form.referralCode}
                  onChange={handleChange}
                  placeholder="추천인 코드가 있으면 입력하세요"
                  className="w-full pl-10 pr-3 h-12 bg-slate-50 dark:bg-[#1c1c27] border border-slate-200 dark:border-[#3b3b54] rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 text-sm"
                />
              </div>
            </div>

            {/* 약관 동의 */}
            <div className="space-y-3 pt-2">
              {/* 이용약관 */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="agreeTerms"
                  checked={agreeTerms}
                  onChange={(e) => setAgreeTerms(e.target.checked)}
                  className="size-4 rounded border-slate-300 text-primary focus:ring-primary cursor-pointer"
                />
                <label htmlFor="agreeTerms" className="text-sm text-slate-700 dark:text-slate-300 cursor-pointer flex-1">
                  <span className="text-red-500 font-bold">[필수]</span> 이용약관에 동의합니다
                </label>
                <button
                  type="button"
                  onClick={onViewTerms}
                  className="text-xs text-primary hover:underline font-medium shrink-0"
                >
                  보기
                </button>
              </div>

              {/* 개인정보 처리방침 */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="agreePrivacy"
                  checked={agreePrivacy}
                  onChange={(e) => setAgreePrivacy(e.target.checked)}
                  className="size-4 rounded border-slate-300 text-primary focus:ring-primary cursor-pointer"
                />
                <label htmlFor="agreePrivacy" className="text-sm text-slate-700 dark:text-slate-300 cursor-pointer flex-1">
                  <span className="text-red-500 font-bold">[필수]</span> 개인정보 처리방침에 동의합니다
                </label>
                <button
                  type="button"
                  onClick={onViewPrivacy}
                  className="text-xs text-primary hover:underline font-medium shrink-0"
                >
                  보기
                </button>
              </div>
            </div>

            {/* 에러 메시지 */}
            {error && (
              <div className="p-3 rounded-xl border bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700 text-red-800 dark:text-red-200 text-sm">
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-base">error</span>
                  <span>{error}</span>
                </div>
              </div>
            )}

            {/* 가입 버튼 */}
            <button
              type="submit"
              disabled={loading || !agreeTerms || !agreePrivacy}
              className="w-full py-4 bg-primary text-white rounded-xl font-bold text-base shadow-lg shadow-primary/40 hover:opacity-90 active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <span className="material-symbols-outlined animate-spin">progress_activity</span>
                  가입 처리 중...
                </>
              ) : (
                <>
                  <span className="material-symbols-outlined">person_add</span>
                  회원가입
                </>
              )}
            </button>
          </form>

          {/* 로그인 링크 */}
          <div className="mt-6 pt-4 border-t border-slate-200 dark:border-[#282839] text-center">
            <button
              onClick={onSwitchToLogin}
              className="text-sm text-primary hover:underline font-medium"
            >
              이미 계정이 있으신가요? 로그인
            </button>
          </div>
        </div>

        <div className="text-center mt-4">
          <p className="text-white/60 text-sm">© 2024 InstaAuto. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}

export default RegisterPage;
